import type { FC } from 'react';
import { useCallback, useState } from 'react';
import type { User } from 'firebase/auth';
import { Divider, Form } from 'antd';
import { useAuth } from '../../../contexts/AuthContext';
import { FormItem } from '../../molecules/FormItem';
import { Input } from '../../atoms/Input';
import { ReactComponent as PersonIcon } from '../../../assets/icons/person.svg';
import { Button } from '../../atoms/Button';
import { GoogleSignInButton } from '../../molecules/GoogleSignInButton';
import { SubmitHandlerValuesProp } from '../SignUpForm';
import { Link } from '../../atoms/Link';


interface LogInFormProps {
  handleSubmit: (values: SubmitHandlerValuesProp) => Promise<void>;
  onGoogleSignInSuccess: (user?: User) => Promise<void>;
}

export const LogInForm: FC<LogInFormProps> = ({ handleSubmit, onGoogleSignInSuccess }) => {
  const { signInWithGoogle } = useAuth();
  const [isPasswordLogInLoading, setIsPasswordLogInLoading] = useState(false);
  const [isGoogleLogInLoading, setIsGoogleLogInLoading] = useState(false);

  const onSubmit = useCallback(async (values: SubmitHandlerValuesProp) => {
    setIsPasswordLogInLoading(true);

    await handleSubmit(values);

    setIsPasswordLogInLoading(false);
  }, [handleSubmit]);

  const handleGoogleSignIn = useCallback(async () => {
    setIsGoogleLogInLoading(true);
    try {
      const result = await signInWithGoogle();

      if (result?.user) {
        await onGoogleSignInSuccess(result?.user);
      }
    } catch (err) {
      console.debug('GoogleSignIn Error: ', err);
    } finally {
      setIsGoogleLogInLoading(false);
    }
  }, [onGoogleSignInSuccess, signInWithGoogle]);

  return (
    <Form
      layout="vertical"
      onFinish={onSubmit}
      requiredMark={false}
      validateTrigger="onBlur"
      disabled={isPasswordLogInLoading || isGoogleLogInLoading}
    >
      <FormItem
        name="email"
        label="Email address"
        rules={[
          { required: true, whitespace: true, message: 'Please enter your email!' },
          { type: 'email', message: 'Please enter a valid email!' },
        ]}
      >
        <Input type="email" suffix={<PersonIcon />} placeholder="Enter Email" autoComplete="email" />
      </FormItem>

      <FormItem
        name="password"
        label="Password"
        rules={[
          { required: true, whitespace: true, message: 'Please enter your password!' },
          { min: 8, message: 'Password must be at least 8 characters!' },
        ]}
      >
        <Input type="password" placeholder="Enter Password" autoComplete="current-password" />
      </FormItem>

      <div style={{ marginTop: 26 }}>
        <Button
          type="primary"
          htmlType="submit"
          size="large"
          block
          disabled={isGoogleLogInLoading}
          loading={isPasswordLogInLoading}
        >
          Login
        </Button>

        <Divider plain style={{ marginTop: 8, marginBottom: 8 }}>
          or
        </Divider>

        <GoogleSignInButton
          size="large"
          onClick={handleGoogleSignIn}
          block
          disabled={isPasswordLogInLoading}
          loading={isGoogleLogInLoading}
        />

        <div style={{ marginTop: 16 }}>
          <Link to="/forgot-password">Forgot password?</Link>
        </div>
      </div>
    </Form>
  );
};
